import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, Pencil, Trash2, FileSignature, Paperclip, Download, X as XIcon, Upload, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

function todayIso() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-ES');
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const emptyForm = () => ({
  id: null,
  title: '',
  customerId: '',
  typeId: '',
  statusId: '',
  contractDate: todayIso(),
  body: '',
  notes: ''
});

const Contracts = () => {
  const { hasPermission } = useAuth();
  const modal = useModal();
  const fileInputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [search, setSearch] = useUserPreference('contracts:search', '');
  const [customerFilter, setCustomerFilter] = useUserPreference('contracts:customer', '');
  const [typeFilter, setTypeFilter] = useUserPreference('contracts:type', '');
  const [statusFilter, setStatusFilter] = useUserPreference('contracts:status', '');
  const [viewMode, setViewMode] = useViewMode('contracts');
  const fl = useFieldLayout('contracts', viewMode);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [attachments, setAttachments] = useState([]);

  const canCreate = hasPermission('contracts', 'create');
  const canEdit = hasPermission('contracts', 'edit');
  const canDelete = hasPermission('contracts', 'delete');

  const loadAll = useCallback(async () => {
    try {
      const [itemsRes, customersRes, typesRes, statusesRes] = await Promise.all([
        api.get('/contracts'),
        api.get('/customers'),
        api.get('/contract-types'),
        api.get('/contract-statuses')
      ]);
      setItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
      setStatuses(Array.isArray(statusesRes.data) ? statusesRes.data : []);
    } catch (error) {
      modal.error('Error cargando contratos');
    } finally {
      setLoading(false);
    }
  }, [modal]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((c) => {
      if (customerFilter && String(c.customerId || '') !== String(customerFilter)) return false;
      if (typeFilter && String(c.typeId || '') !== String(typeFilter)) return false;
      if (statusFilter && String(c.statusId || '') !== String(statusFilter)) return false;
      if (term) {
        const haystack = [
          c.title || '',
          c.body || '',
          c.notes || '',
          c.customer?.name || '',
          c.type?.name || '',
          c.status?.name || ''
        ].join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [items, search, customerFilter, typeFilter, statusFilter]);

  const openCreate = () => {
    setForm(emptyForm());
    setAttachments([]);
    setShowForm(true);
  };

  const openEdit = (contract) => {
    setForm({
      id: contract.id,
      title: contract.title || '',
      customerId: contract.customerId ? String(contract.customerId) : '',
      typeId: contract.typeId ? String(contract.typeId) : '',
      statusId: contract.statusId ? String(contract.statusId) : '',
      contractDate: contract.contractDate ? String(contract.contractDate).slice(0, 10) : todayIso(),
      body: contract.body || '',
      notes: contract.notes || ''
    });
    setAttachments(Array.isArray(contract.attachments) ? contract.attachments : []);
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving || uploading) return;
    setShowForm(false);
    setForm(emptyForm());
    setAttachments([]);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      modal.error('El título es obligatorio');
      return;
    }
    if (!form.contractDate) {
      modal.error('La fecha del contrato es obligatoria');
      return;
    }

    const payload = {
      title: form.title.trim(),
      customerId: form.customerId ? Number(form.customerId) : null,
      typeId: form.typeId ? Number(form.typeId) : null,
      statusId: form.statusId ? Number(form.statusId) : null,
      contractDate: form.contractDate,
      body: form.body,
      notes: form.notes
    };

    setSaving(true);
    try {
      let savedId = form.id;
      if (form.id) {
        await api.put(`/contracts/${form.id}`, payload);
      } else {
        const res = await api.post('/contracts', payload);
        savedId = res.data.id;
        setForm((prev) => ({ ...prev, id: savedId }));
      }
      const refreshed = await api.get(`/contracts/${savedId}`);
      setAttachments(Array.isArray(refreshed.data.attachments) ? refreshed.data.attachments : []);
      await loadAll();
      if (!form.id) {
        setForm({
          id: savedId,
          title: refreshed.data.title || '',
          customerId: refreshed.data.customerId ? String(refreshed.data.customerId) : '',
          typeId: refreshed.data.typeId ? String(refreshed.data.typeId) : '',
          statusId: refreshed.data.statusId ? String(refreshed.data.statusId) : '',
          contractDate: refreshed.data.contractDate ? String(refreshed.data.contractDate).slice(0, 10) : todayIso(),
          body: refreshed.data.body || '',
          notes: refreshed.data.notes || ''
        });
      }
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando el contrato');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (contract) => {
    modal.confirm(
      `¿Eliminar el contrato "${contract.title}"? Se borrarán también sus adjuntos.`,
      async () => {
        try {
          await api.delete(`/contracts/${contract.id}`);
          await loadAll();
          if (form.id === contract.id) closeForm();
        } catch (error) {
          modal.error(error.response?.data?.message || 'Error eliminando el contrato');
        }
      }
    );
  };

  const handleUploadFiles = async (fileList) => {
    if (!form.id) {
      modal.error('Guarda primero el contrato antes de adjuntar archivos');
      return;
    }
    if (!fileList || fileList.length === 0) return;

    const formData = new FormData();
    Array.from(fileList).forEach((file) => formData.append('files', file));

    setUploading(true);
    try {
      await api.post(`/contracts/${form.id}/attachments`, formData);
      const refreshed = await api.get(`/contracts/${form.id}`);
      setAttachments(Array.isArray(refreshed.data.attachments) ? refreshed.data.attachments : []);
      await loadAll();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error subiendo archivos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = (attachment) => {
    if (!form.id) return;
    modal.confirm(
      `¿Eliminar el adjunto "${attachment.fileName}"?`,
      async () => {
        try {
          await api.delete(`/contracts/${form.id}/attachments/${attachment.id}`);
          const refreshed = await api.get(`/contracts/${form.id}`);
          setAttachments(Array.isArray(refreshed.data.attachments) ? refreshed.data.attachments : []);
          await loadAll();
        } catch (error) {
          modal.error(error.response?.data?.message || 'Error eliminando adjunto');
        }
      }
    );
  };

  const downloadAttachment = async (attachment) => {
    if (!form.id) return;
    try {
      const res = await api.get(
        `/contracts/${form.id}/attachments/${attachment.id}/file`,
        { responseType: 'blob' }
      );
      const objUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (error) {
      modal.error(error.response?.data?.message || 'No se pudo descargar el archivo');
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando contratos...</div>;
  }

  const columns = [
    { key: 'title', header: 'Título', className: 'dt-strong', fixed: true, render: (c) => c.title },
    { key: 'customer', header: 'Cliente', render: (c) => c.customer?.name || 'Sin cliente' },
    { key: 'contractDate', header: 'Fecha', className: 'dt-nowrap', render: (c) => formatDate(c.contractDate) },
    { key: 'type', header: 'Tipo', render: (c) => c.type?.name || '—' },
    { key: 'status', header: 'Estado', render: (c) => (c.status?.name ? <span className="badge badge-info">{c.status.name}</span> : '—') },
    { key: 'attachments', header: 'Adjuntos', className: 'num', render: (c) => c.attachments?.length || 0 },
    { key: 'actions', header: '', className: 'col-actions', render: (c) => (
      <div className="dt-actions">
        <button className="btn-icon" onClick={() => openEdit(c)} title={canEdit ? 'Ver / editar' : 'Ver'}><Pencil size={16} /></button>
        {canDelete && (
          <button className="btn-icon" onClick={() => handleDelete(c)} title="Eliminar" style={{ color: '#dc2626' }}><Trash2 size={16} /></button>
        )}
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Contratos</h1>
          <p className="page-subtitle">{filteredItems.length} contrato(s)</p>
        </div>
        {canCreate && (
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={16} /> Nuevo contrato
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <div className="form-group" style={{ position: 'relative', margin: 0 }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: 12, color: '#94a3b8' }} />
            <input
              className="form-input"
              style={{ paddingLeft: 32 }}
              placeholder="Buscar por título, contenido, cliente…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <select className="form-input" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
              <option value="">Todos los clientes</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <select className="form-input" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">Todos los tipos</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <select className="form-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Todos los estados</option>
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <VistaControles
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            fields={fields}
            hidden={fl.hidden}
            onToggleField={fl.toggle}
            onReorderFields={fl.reorder}
          />
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>
          <FileSignature size={36} style={{ opacity: 0.4, marginBottom: 8 }} />
          <div>No hay contratos que coincidan con los filtros.</div>
        </div>
      ) : viewMode === 'table' ? (
        <div className="card">
          <DataTable columns={fl.orderColumns(columns)} rows={filteredItems} hiddenKeys={fl.hidden} />
        </div>
      ) : (
        <div className="items-grid">
          {filteredItems.map((c) => (
            <div className="item-card" key={c.id}>
              <div className="item-card-header">
                <div className="item-card-avatar" style={{ background: 'var(--primary)' }}>
                  <FileSignature size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="item-card-title">{c.title}</div>
                  {!fl.isHidden('customer') && (
                    <div className="item-card-subtitle" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.customer?.name || 'Sin cliente'}
                    </div>
                  )}
                </div>
                {!fl.isHidden('status') && c.status?.name && <span className="badge badge-info">{c.status.name}</span>}
              </div>
              <div className="item-card-body">
                {!fl.isHidden('contractDate') && <div className="item-card-row" style={fl.cardStyle('contractDate')}><span className="item-label">Fecha</span><span>{formatDate(c.contractDate)}</span></div>}
                {!fl.isHidden('type') && c.type?.name && <div className="item-card-row" style={fl.cardStyle('type')}><span className="item-label">Tipo</span><span>{c.type.name}</span></div>}
                {!fl.isHidden('attachments') && <div className="item-card-row" style={fl.cardStyle('attachments')}><span className="item-label">Adjuntos</span><span>{c.attachments?.length || 0}</span></div>}
              </div>
              <div className="item-card-footer">
                <div className="item-card-actions">
                  <button className="btn btn-outline btn-sm" onClick={() => openEdit(c)}>
                    <Pencil size={14} /> {canEdit ? 'Ver / editar' : 'Ver'}
                  </button>
                  {canDelete && (
                    <button className="btn btn-outline btn-sm" onClick={() => handleDelete(c)} style={{ color: '#dc2626' }}>
                      <Trash2 size={14} /> Eliminar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{form.id ? 'Editar contrato' : 'Nuevo contrato'}</h2>
              <button className="btn-icon" onClick={closeForm}><XIcon size={18} /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Título *</label>
                  <input
                    className="form-input"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                    disabled={!canEdit && form.id}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Cliente</label>
                    <select
                      className="form-input"
                      value={form.customerId}
                      onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                      disabled={!canEdit && form.id}
                    >
                      <option value="">Sin cliente</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fecha del contrato *</label>
                    <input
                      type="date"
                      className="form-input"
                      value={form.contractDate}
                      onChange={(e) => setForm({ ...form, contractDate: e.target.value })}
                      required
                      disabled={!canEdit && form.id}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tipo</label>
                    <select
                      className="form-input"
                      value={form.typeId}
                      onChange={(e) => setForm({ ...form, typeId: e.target.value })}
                      disabled={!canEdit && form.id}
                    >
                      <option value="">Sin tipo</option>
                      {types.filter((t) => t.isActive || String(t.id) === String(form.typeId)).map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-input"
                      value={form.statusId}
                      onChange={(e) => setForm({ ...form, statusId: e.target.value })}
                      disabled={!canEdit && form.id}
                    >
                      <option value="">Sin estado</option>
                      {statuses.filter((s) => s.isActive || String(s.id) === String(form.statusId)).map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Texto del contrato</label>
                  <textarea
                    className="form-input"
                    rows={12}
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    placeholder="Cláusulas, contenido completo del contrato…"
                    disabled={!canEdit && form.id}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    disabled={!canEdit && form.id}
                  />
                </div>

                <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 16, paddingTop: 16 }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                    <Paperclip size={14} style={{ display: 'inline', marginRight: 6 }} />
                    Adjuntos ({attachments.length})
                  </h3>

                  {!form.id && (
                    <div style={{ fontSize: '0.85rem', color: '#64748b', padding: 12, background: '#f8fafc', borderRadius: 6 }}>
                      Guarda primero el contrato y luego podrás añadir archivos adjuntos.
                    </div>
                  )}

                  {form.id && (
                    <>
                      {canEdit && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            onChange={(e) => handleUploadFiles(e.target.files)}
                          />
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                          >
                            <Upload size={14} /> {uploading ? 'Subiendo…' : 'Añadir archivos'}
                          </button>
                        </div>
                      )}

                      {attachments.length === 0 ? (
                        <div style={{ fontSize: '0.85rem', color: '#94a3b8', textAlign: 'center', padding: 12 }}>
                          Sin adjuntos
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {attachments.map((att) => (
                            <div
                              key={att.id}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '8px 10px', background: '#f8fafc',
                                borderRadius: 6, fontSize: '0.85rem'
                              }}
                            >
                              <Paperclip size={14} style={{ color: '#64748b', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {att.fileName}
                              </div>
                              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{formatBytes(att.fileSize)}</span>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => downloadAttachment(att)}
                                title="Descargar"
                              >
                                <Download size={14} />
                              </button>
                              {canEdit && (
                                <button
                                  type="button"
                                  className="btn-icon"
                                  onClick={() => handleDeleteAttachment(att)}
                                  style={{ color: '#dc2626' }}
                                  title="Eliminar"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeForm} disabled={saving || uploading}>
                  Cerrar
                </button>
                {canEdit && (
                  <button type="submit" className="btn btn-primary" style={{ marginLeft: 'auto' }} disabled={saving || uploading}>
                    <Save size={14} /> {saving ? 'Guardando…' : 'Guardar'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
